import type { UserManager } from 'oidc-client-ts'
import { me } from 'api/auth'
import { API_CALL_SUCCESS } from 'actions/actionConstants'
import { USER_LOGIN } from 'actions/apiActionConstants'
import type { AppAction } from 'actions/types/ActionTypes'
import { setAuthCookies } from 'cookies/authCookies'
import { setDatadogUser } from 'services/datadogRum'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { markOidcSession } from './authMethod'
import { OidcProvider } from './providers'
import { analyticsSourceForProvider } from './analyticsSource'

// Restores a session whose access token expired while the tab was away but whose
// refresh token is still valid. signinSilent() runs the refresh-token grant
// headlessly, so a hard load that SSR-rendered anonymous recovers without a
// manual reload or a trip through the hosted login. Takes the coordinator's
// long-lived UserManager so its silent-renew re-arms afterwards.
export function recoverOidcSession(userManager: UserManager, provider: OidcProvider): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const regionCode = state.geo.currentRegionCode
    const luxPlusEnabled = getIsLuxPlusEnabled(state)

    const user = await userManager.signinSilent()

    const accessToken = user?.access_token
    if (!accessToken) {
      throw new Error('OIDC silent sign-in returned no access token')
    }

    const account = await me(accessToken, regionCode, luxPlusEnabled)
    // initialiseUser early-returns for the OIDC path, so its loggedIn branch
    // never runs to attribute the traveller.
    setDatadogUser(account.memberId)

    setAuthCookies(accessToken)
    markOidcSession(provider)

    dispatch({
      type: API_CALL_SUCCESS,
      api: USER_LOGIN,
      source: analyticsSourceForProvider(provider),
      data: {
        account,
        accessToken,
        // A rehydration, not an interactive sign-in — post-login side effects
        // must not fire as if the traveller just logged in.
        isSignIn: false,
        authMethod: 'oidc',
        oidcProvider: provider,
      },
    })
  }
}
